import { uploadData } from "aws-amplify/storage";
import awsmobile from "../aws-exports";
import { IMedia, IS3Object } from "./models/Models";
import { Visibility } from "../models";
import { lookupMimeType } from "./MimeTypes";
import { changeFileExtension, getExtension } from "./Utils";
import Rotator from "exif-auto-rotate";

import * as piexif from "./piexif.js";
import { cleanGeoJson } from "./geo/Geo";
import toGeoJSON from "@mapbox/togeojson";
import { Kernel } from "./Kernel";
import { makeS3Key, MediaUploadResult } from "./MediaLibrary";
import { getModuleLog } from "./Logger";
import { DateTime } from "luxon";

const log = getModuleLog("Media");

export const MAX_HIRES_IMAGE_WIDTH = 4096;
export const MAX_LORES_IMAGE_WIDTH = 1080;
export const MAX_AVATAR_SIZE = 1024 * 1024;
export const MAX_HIRES_IMAGE_SIZE = 8 * 1024 * 1024;
export const MAX_LORES_IMAGE_SIZE = 2 * 1024 * 1024;
export const MAX_VIDEO_SIZE_MB = 250;
export const MAX_VIDEO_SIZE = MAX_VIDEO_SIZE_MB * 1024 * 1024;

type UploadResult = {
    object: IS3Object;
    mimeType: string;
};

interface GpsOutput {
    latitude: number;
    longitude: number;
}

export enum MediaType {
    Unknown,
    Image,
    Video,
    Pdf,
    Kml,
    GeoJson,
    Svg,
    YouTube,
    Gpx,
    SketchFab,
}

export const GEOMETRY_MEDIA_TYPES = [MediaType.Kml, MediaType.GeoJson, MediaType.Gpx];

const MediaTypeToString = new Map<MediaType, string>([
    [MediaType.Unknown, "unknown"],
    [MediaType.Image, "image"],
    [MediaType.Video, "video"],
    [MediaType.Pdf, "pdf"],
    [MediaType.Kml, "kml"],
    [MediaType.GeoJson, "geojson"],
    [MediaType.Svg, "svg"],
    [MediaType.YouTube, "youtube"],
    [MediaType.Gpx, "gpx"],
    [MediaType.SketchFab, "sketchfab"],
]);

const StringToMediaType = new Map<string, MediaType>([
    ["unknown", MediaType.Unknown],
    ["image", MediaType.Image],
    ["video", MediaType.Video],
    ["pdf", MediaType.Pdf],
    ["kml", MediaType.Kml],
    ["geojson", MediaType.GeoJson],
    ["svg", MediaType.Svg],
    ["youtube", MediaType.YouTube],
    ["gpx", MediaType.Gpx],
    ["sketchfab", MediaType.SketchFab],
]);

export const PHOTO_MEDIA_TYPES = [
    MediaType.Image,
    MediaType.Svg,
    MediaType.Video,
    MediaType.YouTube,
    MediaType.Pdf,
    MediaType.SketchFab,
];
export const GEO_MEDIA_TYPES = [MediaType.GeoJson, MediaType.Kml];
export const NOT_DOWNLOADABLE_TYPES = [MediaType.YouTube, MediaType.SketchFab];

export const MEDIA_GALLERY_VIEW_TYPES = PHOTO_MEDIA_TYPES.map(
    (x) => mediaTypeToString(x) as string
);
export const MEDIA_GALLERY_SURVEY_TYPES = GEO_MEDIA_TYPES.map(
    (x) => mediaTypeToString(x) as string
);

export function isGeoMediaType(type: MediaType) {
    return GEO_MEDIA_TYPES.includes(type);
}

export function isGeoMimeType(type: string) {
    return GEO_MEDIA_TYPES.includes(getMediaTypeFromMimeType(type));
}

export function mediaCanBeDownloaded(media: IMedia) {
    const mediaType = getMediaTypeFromMimeType(media.mimeType);
    return !NOT_DOWNLOADABLE_TYPES.includes(mediaType);
}

export function mediaTypeToString(type: MediaType): string | undefined {
    if (!MediaTypeToString.has(type)) {
        return undefined;
    }
    return MediaTypeToString.get(type);
}

export function stringToMediaType(type: string): MediaType | undefined {
    if (!StringToMediaType.has(type)) {
        return undefined;
    }
    return StringToMediaType.get(type);
}

export function getMediaTypeStringFromMimeType(mimeType: string) {
    return mediaTypeToString(getMediaTypeFromMimeType(mimeType));
}

export function getMimeTypeFromExtension(ext: string) {
    return lookupMimeType(ext);
}

export function getMediaTypeFromMimeType(mimeType: string): MediaType {
    const parts = mimeType.split("/");
    if (parts && parts.length === 2) {
        const type = parts[0];
        switch (type) {
            case "image":
                if (parts[1] === "svg+xml") {
                    return MediaType.Svg;
                }
                return MediaType.Image;
            case "video":
                return MediaType.Video;
            case "application": {
                switch (parts[1]) {
                    case "pdf":
                        return MediaType.Pdf;
                    case "vnd.google-earth.kml+xml":
                        return MediaType.Kml;
                    case "json":
                        return MediaType.GeoJson;
                    case "youtube":
                        return MediaType.YouTube;
                    case "sketchfab":
                        return MediaType.SketchFab;
                    case "geo+json":
                        return MediaType.GeoJson;
                    case "gpx":
                    case "gpx+xml":
                        return MediaType.Gpx;
                }
            }
        }
    }

    return MediaType.Unknown;
}

export function getMediaOfType(media: IMedia[], types: MediaType[]): IMedia[] {
    return media.filter((x) => {
        if (x.mimeType) {
            const type = getMediaTypeFromMimeType(x.mimeType);
            if (type && types.includes(type)) {
                return true;
            }
        }
        return false;
    });
}

export function numMediaOfType(media: IMedia[], types: MediaType[]): number {
    return getMediaOfType(media, types).length;
}

export async function exifLocation(blob: any): Promise<GpsOutput | undefined> {
    try {
        return await (await import("exifr")).gps(blob);
    } catch (_e) {
        return undefined;
    }
}

export async function exifDateTimeOriginal(blob: any): Promise<string | undefined> {
    try {
        const exifData = await (await import("exifr")).parse(blob, { tiff: false, exif: true });
        if (exifData && exifData.DateTimeOriginal) {
            // Parse the DateTimeOriginal with Luxon to handle time zone
            const dateTimeOriginal = DateTime.fromJSDate(
                exifData.DateTimeOriginal,

                { zone: "utc" }
            );
            return dateTimeOriginal.toISO();
        } else {
            log.debug("DateTimeOriginal not found in EXIF data");
            return undefined;
        }
    } catch (error) {
        log.debug("Error reading EXIF data:", error);
        return undefined;
    }
}

const makeUploadError = (error: string) => {
    return { succeeded: false, error } as MediaUploadResult;
};

export async function uploadMediaEventHandler(event: any): Promise<MediaUploadResult> {
    try {
        if (event.target.files && event.target.files.length) {
            const file = event.target.files[0];
            if (file) {
                return await uploadMedia(file);
            }
        }
    } catch (error: any) {
        return makeUploadError(error);
    }

    return makeUploadError("Error uploading media");
}

export async function removeExif(inputFile: File): Promise<File> {
    // first we need to correctly orientate the image as we are about to strip this data
    const orientatedBlob = await Rotator.createRotatedImageAsync(inputFile, "blob");
    inputFile = new File([orientatedBlob], inputFile.name, {
        type: "image/jpeg",
        lastModified: inputFile.lastModified,
    });

    return new Promise((resolve, reject) => {
        if (!inputFile.type.match("image/jpeg.*")) {
            reject();
        }

        const reader = new FileReader();
        reader.onloadend = function (e) {
            const jpeg = piexif.remove(e.target?.result);

            const str = atob(jpeg.split(",")[1]);
            const data = [];
            for (let p = 0; p < str.length; p++) {
                data[p] = str.charCodeAt(p);
            }
            const ua = new Uint8Array(data);
            const blob = new Blob([ua], { type: "image/jpeg" });
            const resultFile = new File([blob], inputFile.name, {
                type: "image/jpeg",
                lastModified: inputFile.lastModified,
            });
            resolve(resultFile);
        };
        reader.readAsDataURL(inputFile);
    });
}

export async function rescaleImage(
    file: Blob,
    maxWidth: number,
    maxHeight: number,
    maxSizeMB: number
): Promise<Blob> {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    const reader = new FileReader();
    const imageDataUrl: string = await new Promise((resolve, reject) => {
        reader.readAsDataURL(file);
        reader.onload = () => {
            if (reader.result) {
                resolve(reader.result as string);
            } else {
                reject(new Error("Failed to read file."));
            }
        };
        reader.onerror = reject;
    });

    const img = new Image();
    img.src = imageDataUrl;

    await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
        throw new Error("Failed to get canvas context.");
    }

    const aspectRatio = img.width / img.height;
    let width = img.width;
    let height = img.height;
    let quality = 1.0;

    if (width > maxWidth || height > maxHeight) {
        if (width / maxWidth > height / maxHeight) {
            width = maxWidth;
            height = width / aspectRatio;
        } else {
            height = maxHeight;
            width = height * aspectRatio;
        }
    }

    const resizeAndCompress = async (): Promise<Blob> => {
        canvas.width = width;
        canvas.height = height;

        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        const blob: Blob = await new Promise((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error("Failed to create blob from canvas."));
                    }
                },
                "image/jpeg",
                quality
            );
        });

        if (blob.size > maxSizeBytes && quality > 0.1) {
            quality -= 0.1; // Reduce quality in steps
            return resizeAndCompress();
        } else {
            log.info(
                `Resized image to ${width}x${height} with quality ${quality} @ ${(blob.size / (1024 * 1024)).toFixed(2)}MB`
            );
            return blob;
        }
    };

    return resizeAndCompress();
}

async function drawTextWatermark(blob: Blob, watermarkText: string): Promise<Blob> {
    const reader = new FileReader();
    const imageDataUrl: string = await new Promise((resolve, reject) => {
        reader.readAsDataURL(blob);
        reader.onload = () => {
            if (reader.result) {
                resolve(reader.result as string);
            } else {
                reject(new Error("Failed to read blob."));
            }
        };
        reader.onerror = reject;
    });

    const img = new Image();
    img.src = imageDataUrl;

    await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
        throw new Error("Failed to get canvas context.");
    }

    canvas.width = img.width;
    canvas.height = img.height;

    ctx.drawImage(img, 0, 0, img.width, img.height);

    // Set watermark style
    const fontSize = Math.floor(canvas.width / 20);
    ctx.font = `${fontSize}px Arial`;
    ctx.fillStyle = "rgba(255, 255, 255, 0.5)"; // White color with 50% opacity
    ctx.textAlign = "right";
    ctx.textBaseline = "bottom";

    // Draw watermark text
    const padding = 10;
    ctx.fillText(watermarkText, canvas.width - padding, canvas.height - padding);

    const resultBlob: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error("Failed to create blob from canvas."));
            }
        }, "image/jpeg");
    });

    return resultBlob;
}

export type ImageUploadOption = {
    highRes: boolean;
    watermark?: string;
};

export async function processImageForUpload(
    blob: Blob | File,
    options: ImageUploadOption = {
        highRes: false,
    }
) {
    const maxWidth = options.highRes ? MAX_HIRES_IMAGE_WIDTH : MAX_LORES_IMAGE_WIDTH;
    const maxSize = options.highRes ? MAX_HIRES_IMAGE_SIZE : MAX_LORES_IMAGE_SIZE;
    blob = await rescaleImage(blob, maxWidth, maxWidth, maxSize);

    if (options.watermark) {
        blob = await drawTextWatermark(blob, options.watermark);
    }
    return blob;
}

export async function uploadMedia(
    file: File,
    options: ImageUploadOption = {
        highRes: false,
    },
    data?: any
): Promise<MediaUploadResult> {
    let name = file.name;
    const size = file.size;
    let mimeType: string | undefined = file.type;

    if (!mimeType || mimeType.length === 0) {
        mimeType = getMimeTypeFromExtension(`.${getExtension(name)}`);
        if (!mimeType) {
            return makeUploadError(`Media type not supported`);
        }
    }

    try {
        const type = getMediaTypeFromMimeType(mimeType);

        if (type === MediaType.Unknown) {
            return makeUploadError(`Media type not supported`);
        } else if (type === MediaType.Video && size > MAX_VIDEO_SIZE) {
            return makeUploadError(`Video exceeds maximum size of 250mb`);
        }

        let blob: Blob;

        if (type === MediaType.Image) {
            blob = await processImageForUpload(file, options);
            const maxWidth = options.highRes ? MAX_HIRES_IMAGE_WIDTH : MAX_LORES_IMAGE_WIDTH;
            const maxSize = options.highRes ? MAX_HIRES_IMAGE_SIZE : MAX_LORES_IMAGE_SIZE;
            blob = await rescaleImage(file, maxWidth, maxWidth, maxSize);

            if (options.watermark) {
                blob = await drawTextWatermark(blob, options.watermark);
            }
            name = changeFileExtension(name, "jpg");
        } else {
            blob = new Blob([new Uint8Array(await file.arrayBuffer())], {
                type: file.type,
            });
        }

        if (blob) {
            const result = await Kernel.instance().mediaLibrary.upload(
                blob,
                Visibility.PUBLIC,
                name,
                mimeType,
                data
            );

            if (result && result.succeeded && result.media) {
                if (type === MediaType.Image) {
                    const thumbnailResult = await createMissingThumbnails(result.media);
                    if (thumbnailResult && thumbnailResult.updated) {
                        result.media = thumbnailResult.media;
                    }
                }
                return result;
            }
        }
    } catch (error: any) {
        log.error(`Error uploading media: ${error}`);
    }
    return makeUploadError("Error uploading media");
}

export async function uploadMediaToS3(
    file: any,
    visibility: Visibility,
    progressCallback: any | undefined,
    name: string,
    mimeType: string | undefined
): Promise<UploadResult | undefined> {
    if (file) {
        if (!mimeType || mimeType.length === 0) {
            mimeType = lookupMimeType(name);
        }
        if (name && mimeType) {
            const key = await makeS3Key(name);
            if (key) {
                const operation = uploadData({
                    key,
                    data: file,
                    options: {
                        accessLevel: visibility === "public" ? "guest" : "private",
                        contentType: mimeType,
                        onProgress: ({ transferredBytes, totalBytes }) => {
                            if (progressCallback && totalBytes) {
                                progressCallback(transferredBytes / totalBytes);
                            }
                        },
                    },
                });
                await operation.result;

                return {
                    object: {
                        key,
                        bucket: awsmobile.aws_user_files_s3_bucket,
                        region: awsmobile.aws_user_files_s3_bucket_region,
                    },
                    mimeType,
                } as UploadResult;
            }
        }
    }

    return undefined;
}

export async function loadGeoJsonFile(file: IMedia) {
    const type = getMediaTypeFromMimeType(file.mimeType);
    if (GEOMETRY_MEDIA_TYPES.includes(type)) {
        const blob = await Kernel.instance().mediaLibrary.get(file.file.key);
        if (blob) {
            const text = await blob.text();
            switch (type) {
                case MediaType.Gpx: {
                    const parser = new DOMParser();
                    const gpx = parser.parseFromString(text, "text/xml");
                    return cleanGeoJson(toGeoJSON.gpx(gpx, { styles: true }));
                }
                case MediaType.Kml: {
                    const parser = new DOMParser();
                    const kml = parser.parseFromString(text, "text/xml");
                    return cleanGeoJson(toGeoJSON.kml(kml, { styles: true }));
                }
                case MediaType.GeoJson:
                    return JSON.parse(text);
            }
        }
    }
    return undefined;
}

export type CreateMissingThumbnailsResult = {
    media: IMedia;
    updated: boolean;
};

export async function createMissingThumbnails(
    media: IMedia
): Promise<CreateMissingThumbnailsResult> {
    if (getMediaTypeFromMimeType(media.mimeType) !== MediaType.Image) {
        return { media, updated: false };
    }

    let updated = false;
    const sizes = [256, 512, 1024];
    let blob: Blob | undefined;
    for (const size of sizes) {
        const prop = `thumbnail_${size}px`;
        if (!(media as any)[prop]) {
            if (blob === undefined) {
                blob = await Kernel.instance().mediaLibrary.get(media.file.key);
            }
            if (blob) {
                blob = await rescaleImage(blob, size, size, 2 * 1024 * 1024);
                const name = `${media.name}_${media.id}_${size}px.jpg`;
                const result = await Kernel.instance().mediaLibrary.upload(
                    blob,
                    Visibility.PUBLIC,
                    name,
                    "image/jpg",
                    undefined
                );
                if (result?.succeeded) {
                    (media as any)[prop] = result.media?.file;
                }
                console.log(`created thumbnail - ${name}`);
                updated = true;
            }
        }
    }
    return {
        media,
        updated,
    };
}
